/**
 * TCS — keyboard route to the PDP gallery zoom dialog (V04-01, R-025 PENDING).
 *
 * WHY THIS EXISTS
 * In carousel presentation Horizon opens <zoom-dialog> only from
 * `on:click="#zoom-dialog-…/open/N"` on the non-focusable <slideshow-slide>
 * (snippets/product-media-gallery-content.liquid). The focusable per-media zoom
 * button exists only in grid presentation. Observed on the storefront
 * 2026-09-14: the gallery's tab stops are the thumbnails and nothing else, and
 * no key press ever opened zoom (WCAG 2.1.1).
 *
 * HOW IT WORKS — and why not a core edit
 * snippets/tcs-pdp-media-zoom.liquid renders one hidden native <button> inside
 * <slideshow-container> (R-003 pass-through). This element reveals it once the
 * gallery, slideshow and zoom-dialog definitions are ready and the closest
 * <media-gallery> exposes an initialized zoom dialog, and on click calls Horizon's
 * own public MediaGallery#zoom(index, event) with the slideshow's current index
 * — the same path Horizon's grid zoom button uses. A button target takes
 * ZoomDialog#open's early (non-view-transition) branch, which never calls
 * selectThumbnail, so the button then calls the public ZoomDialog#selectThumbnail
 * to set the dialog thumbnails' aria-current. Nothing in media-gallery.js,
 * zoom-dialog.js or slideshow.js is patched, overridden or observed.
 *
 * MORPH: the button carries data-skip-node-update, so Horizon's morph (quick-add
 * content, variant section updates) does not copy the server-rendered `hidden`
 * back onto a button this element has already revealed (a worst-case guard: the Liquid
 * <media-gallery> carries data-skip-subtree-update, so morph normally skips it). Side
 * effect: a morph cannot change this button's attributes either, so a later server-side
 * change to its class or ARIA needs a full gallery replacement. The dialog is resolved
 * at click time, so a morphed or replaced <zoom-dialog> is never stale.
 *
 * QUICK-ADD: Horizon makes the quick-add gallery deliberately non-interactive
 * (snippets/quick-add-modal-styles.liquid: pointer-events: none, stacked slides,
 * no arrows, no pointer zoom). The control is therefore not revealed inside
 * <quick-add-dialog>; the enlarged view stays on the product page (agent CTO
 * decision 2026-09-14: no 2.1.1 gap where pointer users have no zoom either).
 * UPGRADE: remove this exclusion if Horizon makes the quick-add gallery interactive.
 *
 * FOCUS — what is preserved and what is added
 * Initial focus (Close) and restoration on close stay native: showModal() moves
 * focus in and dialog.close() hands it back to the element focused before —
 * this button when it opened the dialog. Only for an open this button actually
 * started, and only if focus has nevertheless fallen to <body> after `close`, it
 * is put back on the button. A pointer open on a slide never triggers that
 * fallback. The button stays enabled on 3D-model slides: the dialog renders
 * every media type, and a disabled control here would strand focus after dialog
 * scrolling.
 *
 * Horizon 4.1.3 coupling (re-check on upgrade): MediaGallery#zoom, its
 * `slideshow` getter, Slideshow#current, ZoomDialog#selectThumbnail,
 * ref="zoomDialogComponent" / ref="dialog" on <zoom-dialog>, morph's
 * data-skip-node-update, and the quick-add-dialog element name. If upstream adds
 * a focusable carousel zoom control, remove this element and R-025 item 1.
 */

class TcsPdpMediaZoom extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const { signal } = controller;
    const button = this.querySelector('button');
    if (button) button.hidden = true;

    // Markup alone is not readiness. Keep the control hidden if a dependency
    // fails, and still recover when a slow module eventually becomes available.
    Promise.all(
      [
        'media-gallery',
        'slideshow-component',
        this.closest('media-gallery')?.hasAttribute('data-tcs-image-viewer')
          ? 'tcs-pdp-image-viewer-component'
          : 'zoom-dialog',
      ].map((name) => customElements.whenDefined(name)),
    ).then(() => {
      if (signal.aborted) return;
      const gallery = this.closest('media-gallery');
      const viewer = gallery?.querySelector('tcs-pdp-image-viewer-component');
      if (viewer && !viewer.ready) {
        viewer.addEventListener('tcs:viewer-ready', () => this.#wire(signal), {
          once: true,
          signal,
        });
        return;
      }
      this.#wire(signal);
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @param {Element} gallery */
  static #zoomParts(gallery) {
    const zoomDialog = gallery.refs?.zoomDialogComponent ?? gallery.querySelector('zoom-dialog');
    const dialog = zoomDialog?.refs?.dialog ?? zoomDialog?.querySelector('dialog');
    return typeof zoomDialog?.open === 'function' &&
      typeof zoomDialog?.selectThumbnail === 'function' &&
      dialog instanceof HTMLDialogElement
      ? { zoomDialog, dialog }
      : null;
  }

  /** @param {AbortSignal} signal */
  #wire(signal) {
    const button = this.querySelector('button');
    const gallery = this.closest('media-gallery');
    if (!button || !gallery || typeof gallery.zoom !== 'function') return;
    if (gallery.closest('quick-add-dialog')) return;
    if (!TcsPdpMediaZoom.#zoomParts(gallery)) return;

    button.addEventListener(
      'click',
      (event) => {
        const parts = TcsPdpMediaZoom.#zoomParts(gallery);
        if (!parts) return;
        const { zoomDialog, dialog } = parts;
        const index = Number(gallery.slideshow?.current) || 0;

        gallery.zoom(index, event);
        if (!dialog.open) return;

        zoomDialog.selectThumbnail?.(index, { behavior: 'instant' });
        dialog.addEventListener(
          'close',
          () => {
            requestAnimationFrame(() => {
              if (signal.aborted || !button.isConnected || button.hidden) return;
              const active = document.activeElement;
              if (active === null || active === document.body) button.focus();
            });
          },
          { once: true, signal },
        );
      },
      { signal },
    );

    button.hidden = false;
  }
}

if (!customElements.get('tcs-pdp-media-zoom')) {
  customElements.define('tcs-pdp-media-zoom', TcsPdpMediaZoom);
}
